import * as React from "react";
import Link, { LinkProps } from "next/link";
import { useRouter } from "next/router";
import { useOfferMode } from "@nju/data-access-portal-offers";
import { pushSimpleEvent } from "@nju/data-layer";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { A, BlockText, Panel, Separator, Text } from "@nju/ui";
import { formatPrice } from "@nju/utils";
import { Marker } from "./Marker";

export interface IBlikPaymentBannerProps {
  isActive: boolean;
  amount: string;
  changePaymentMethodUrl?: LinkProps["href"];
  userId: string | undefined;
}

export function BlikPaymentBanner({
  isActive,
  amount,
  changePaymentMethodUrl,
  userId,
}: IBlikPaymentBannerProps) {
  const router = useRouter();
  const mode = useOfferMode();

  const accountAmount = React.useMemo(() => {
    return formatPrice(+amount);
  }, [amount]);

  const handlePushDataLayer = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "payment_deposit",
    });
  }, []);

  const isChangePaymentProcess =
    portalRoutes.mojeKonto.platnosci.index.hasParam(
      router.query,
      "zmianaMetodyPlatnosci",
    );

  return (
    <Panel border={isActive ? "info" : "default"} testId="blik-payment-banner">
      <div className="flex items-center justify-between">
        <Marker isActive={isActive} url={changePaymentMethodUrl} />
        <Icon height={36} name="wallet" width={36} />
      </div>
      <Separator size="s" tone="invisible" />
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "xl" }}>
        Twoje środki: <Text size="keyM">{accountAmount}</Text> zł
      </BlockText>
      <Separator size="s" tone="invisible" />
      <div className="grid gap-2">
        <Link
          href={portalRoutes.mojeKonto.platnosci.dodajSrodki.get({
            ...(isChangePaymentProcess && { zmianaMetodyPlatnosci: "1", mode }),
            ...(userId && { userId }),
          })}
          passHref={true}
        >
          <A onClick={handlePushDataLayer} size={{ "@onlyMobile": "s" }}>
            wpłać środki
          </A>
        </Link>
        <Link
          href={portalRoutes.mojeKonto.platnosci.wyplacSrodki.get({
            ...(isChangePaymentProcess && { zmianaMetodyPlatnosci: "1", mode }),
            ...(userId && { userId }),
          })}
          passHref={true}
        >
          <A
            isDisabled={!+amount}
            onClick={() => {
              pushSimpleEvent({
                event: "activity_service",
                eventName: "payment_pay_out",
              });
            }}
            size={{ "@onlyMobile": "s" }}
            variant="secondary"
          >
            wypłać środki
          </A>
        </Link>
      </div>
    </Panel>
  );
}
